import React, { type ReactNode } from "react";
import type { PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import { capitalize } from "diagnostics-lib/string";
import type { CustomTranslations } from "../../../../i18n/types";

const fr = {
  "project.event.active": "Actif",
  "project.event.active.state": "État",
  "project.event.diagnosed_tab.blank_state.any_pi.action": "Accéder aux réglages",
  "project.event.diagnosed_tab.blank_state.any_pi.subtitle":
    "Pour afficher l'évolution de l'impact de cet événement sur un ou plusieurs indicateurs de performance, ajoutez-les dans les réglages du projet",
  "project.event.diagnosed_tab.blank_state.any_pi.title":
    "Impossible d'afficher l'évolution de l'impact",
  "project.event.header.button.activity.tooltip": "Activité de l’événement",
  "project.event.header.button.comments.tooltip": "Commentaires de l’événement",
  "project.event.header.card.action_identified.title.text": "Actions identifiées",
  "project.event.header.card.action_identified.update.text": "Mettre à jour",
  "project.event.header.card.last_comment.access.text": "Accéder",
  "project.event.header.card.last_comment.empty.text":
    "Soyez le premier à commenter cet événement.",
  "project.event.header.card.last_comment.title.text": "Dernier commentaire",
  "project.event.header.card.resume.duration.text": (
    duration: ReactNode,
    firstDetection: string,
    lastDetection: string | null,
  ) => {
    if (duration === null || lastDetection === null) {
      return <>Première détection : {firstDetection}</>;
    }
    return (
      <>
        Durée : {duration} (première détection : {firstDetection} - dernière détection :{" "}
        {lastDetection})
      </>
    );
  },
  "project.event.header.card.resume.fault.text": (faultName: ReactNode, faultCount: number) => (
    <>
      {faultCount > 1 ? "Défaillances" : "Défaillance"} : <strong>{faultName}</strong>
    </>
  ),
  "project.event.header.card.resume.history.label": "Historique :",
  "project.event.header.card.resume.history.link": (historyCount: number) =>
    `${historyCount} similaire${historyCount > 1 ? "s" : ""}`,
  "project.event.header.card.resume.see_more.text": "Voir tout",
  "project.event.header.card.resume.title.text": "Résumé",
  "project.event.header.description.section.action_plan.title": "Plan d'action",
  "project.event.header.description.section.average_power_loss.title":
    "Pertes de puissance moyennes",
  "project.event.header.description.section.average_power_loss.tooltip":
    "La moyenne se base sur les détections depuis le début de l'événement. La valeur moyenne présentée pondère les valeurs de pertes en fonction de la durée entre 2 points et considère une absence de détection comme une valeur de pertes nulle.",
  "project.event.header.description.section.duration.title": "Durée",
  "project.event.header.description.section.fault.title": "Défaillance",
  "project.event.header.description.section.total_energy_losses.title": "Énergie totale perdue",
  "project.event.header.description.section.total_energy_losses.tooltip":
    "Le calcul de l’énergie intègre les pertes depuis le début de l'événement, en considérant une absence de détection comme une valeur de pertes nulle et en pro-ratisant les pertes selon le facteur de charge calculé sur la période.",
  "project.event.inactive": "Inactif",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metadata.actions.delete":
    "Supprimer",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metadata.actions.edit":
    "Modifier",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metadata.actions.impossible_to_delete":
    "Impossible de supprimer cette période car c'est la seule qui existe",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metatadata.end_date":
    "Fin de la période",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metatadata.start_date":
    "Début de la période",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.modal.add.title":
    "Ajouter une période",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.modal.delete.title":
    "Supprimer la période",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.modal.edit.title":
    "Modifier la période",
  "project.event.manual_fault.modalForm.time_window.delete.description":
    "Souhaitez-vous supprimer la période et les impacts associés :",
  "project.event.manual_fault.modalForm.time_window.delete.message.warning.next_period":
    "La suppression de cette période entraînera le remplacement de ses valeurs d'impact par celles de la période suivante.",
  "project.event.manual_fault.modalForm.time_window.delete.message.warning.previous_period":
    "La suppression de cette période entraînera le remplacement de ses valeurs d'impact par celles de la période précédente.",
  "project.event.manual_fault.modalForm.time_window.delete.success":
    "La suppression de la période a réussi",
  "project.event.manual_fault.side_panel.details.anomaly": "Nom de l’anomalie",
  "project.event.manual_fault.side_panel.details.hitory.tooltip.info": () => (
    <>
      <strong>Défaillance</strong> : inclut les informations relatives à la défaillance, tel que
      défini dans le paramétrage. <br />
      <strong>Historique</strong> : présente les autres événements manuels liés à la même anomalie
      (liste restreinte aux projets de production).
    </>
  ),
  "project.event.manual_fault.side_panel.history.blank_state.subtitle":
    "Si un événement similaire est détecté sur un projet de production, le lien vers cet événement apparaîtra ici.",
  "project.event.manual_fault.side_panel.history.blank_state.title":
    "Aucun événement similaire détecté",
  "project.event.manual_fault.tab.diagnosis.evolution_chart.impact_time_windows_change.description":
    "L'impact de la période la plus récente est appliqué automatiquement aux nouvelles détections jusqu'à la clôture de l'événement.",
  "project.event.manual_fault.tab.diagnosis.evolution_chart.impact_time_windows_change.title":
    "Synthèse de l'impact :",
  "project.event.manual_fault.tab.diagnosis.evolution_chart.impact_time_windows_change.title.button.add_time_window":
    "Période",
  "project.event.manual_fault.tables.impacts_time_windows_change.label": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `Impact sur ${quantityName}`,
  "project.event.side_panel.activity.project_link": "Ouvrir l’activité du projet",
  "project.event.side_panel.activity.title": "Activité de l’événement",
  "project.event.side_panel.comments.project_link": "Ouvrir les commentaires du projet",
  "project.event.side_panel.comments.title": "Commentaires de l’événement",
  "project.event.side_panel.history.filter.detection": "Détection",
  "project.event.side_panel.history.filter.detection.label": ({
    label,
  }: {
    label: boolean;
  }): string => (label ? "Vraie" : "Fausse"),
  "project.event.side_panel.history.filter.fault_event_status": "Statut",
  "project.event.side_panel.tab.history": (historyCount: number) => `Historique (${historyCount})`,
  "project.event.type.diagnosed": "Diagnostiqué",
  "project.event.type.manual": "Manuel",
  "project.event.type.multi_fault": "Événement multi-défaillances",
  "project.events.drawer.create_manual_event.closed.error":
    "La date de clôture doit être située après la date de début",
  "project.events.drawer.create_manual_event.closed.error.before_window":
    "La date de clôture doit être après la date de début de la dernière fenêtre temporelle",
  "project.events.drawer.create_manual_event.closed.start.error":
    "La date de fin doit être située entre la date de début et la date de fin de la source de données",
  "project.events.drawer.create_manual_event.date_picker.closedAt.label": "Date de clôture",
  "project.events.drawer.create_manual_event.date_picker.startsAt.label": "Date de début",
  "project.events.drawer.create_manual_event.description.error.max_characters":
    "La description ne peut pas dépasser 4000 caractères.",
  "project.events.drawer.create_manual_event.error.manual_fault_name_already_exists":
    "Un problème connu porte déjà ce nom (créé automatiquement depuis un événement). Choisissez un nom différent pour l’événement.",
  "project.events.drawer.create_manual_event.error.overlapping_manual_fault_event":
    "Un autre événement lié à ce problème couvre déjà cette période. Modifiez les dates pour éviter le chevauchement.",
  "project.events.drawer.create_manual_event.input.name": "Nom de l’événement",
  "project.events.drawer.create_manual_event.section.anomaly.description":
    "Pour faciliter le suivi, associez cet événement à un problème connu. Sinon, un nouveau type de problème sera créé automatiquement avec le nom de cet événement.",
  "project.events.drawer.create_manual_event.section.anomaly.radio.label.no": "Non",
  "project.events.drawer.create_manual_event.section.anomaly.radio.label.yes": "Oui",
  "project.events.drawer.create_manual_event.section.anomaly.select.impacted_system.label":
    "Système impacté",
  "project.events.drawer.create_manual_event.section.anomaly.select.label": "Problème connu",
  "project.events.drawer.create_manual_event.section.anomaly.select.system.label": "Système",
  "project.events.drawer.create_manual_event.section.anomaly.title": "Associer à un problème connu",
  "project.events.drawer.create_manual_event.section.indicator_performance.title":
    "Impact sur les indicateurs de performance",
  "project.events.drawer.create_manual_event.section.indicator_performance.title.tooltip":
    "Cette valeur sera appliquée à toutes les analyses depuis la date de début, ainsi que pour les prochaines analyses. Elle pourra être revue au sein de l'événement.",
  "project.events.drawer.create_manual_event.select.priority": "Priorité",
  "project.events.drawer.create_manual_event.select.status": "Statut",
  "project.events.drawer.create_manual_event.start.error":
    "La date de début doit être située entre le premier et le dernier point de données lié à la source de données",
  "project.events.drawer.create_manual_event.start.error.after_closed":
    "La date de début doit être avant la date de clôture",
  "project.events.drawer.create_manual_event.start.error.after_window":
    "La date de début doit être située avant la date de début de la période suivante",
  "project.events.drawer.create_manual_event.start.error.missing":
    "La date de début est obligatoire",
  "project.events.drawer.create_manual_event.toast.success": (eventName: ReactNode) => (
    <>L’événement manuel {eventName} a été créé</>
  ),
  "project.events.manualFaultEvent.modal_form.add_period.toast.success": "La période a été ajoutée",
  "project.events.manualFaultEvent.modifyModalForm.errors.should_be_digital": "Doit être un nombre",
  "project.events.manualFaultEvent.modifyModalForm.impactedPIs":
    " Impact sur les indicateurs de performance : ",
  "project.events.manualFaultEvent.modifyModalForm.impactedPeriod": "Période impactée : ",
  "project.events.manualFaultEvent.modifyModalForm.input.placeholder": "Valeur",
  "project.events.manualFaultEvent.modifyModalForm.toast.success": "La période a été mise à jour",
  "project.events.manual_fault.impact_time_windows_change.datepicker.startsAt.label":
    "Début de période",
  "project.events.manual_fault.window.startsAt.error.after_latest_analysis": (formatDate: string) =>
    `La date de début de la période doit être située avant la date de la dernière analyse du projet (${formatDate})`,
  "project.events.manual_fault.window.startsAt.error.before_event_start": (formatDate: string) =>
    `La date de début de la période doit être située après la date de début de l’événement (${formatDate})`,
  "project.events.manual_fault.window.startsAt.error.next_existing_period": (formatDate: string) =>
    `La date de début de la période doit être située avant la date de début de la période suivante dans l’événement (${formatDate})`,
  "project.events.manual_fault.window.startsAt.error.previous_existing_period": (
    formatDate: string,
  ) =>
    `La date de début de la période doit être située après la date de début de la période précédente dans l’événement (${formatDate})`,
  "project.events.modalForm.addPeriod.description":
    "Renseignez la date de début et les impacts à appliquer :",
  "project.events.modalForm.okButton.add": "Ajouter",
  "project.events.modalForm.okButton.delete": "Supprimer",
  "project.events.modalForm.okButton.modify": "Modifier",
  "project.events.tag.manual": "Manuel",
  "project.fault_event.badge.tooltips.single": "1 seule détection",
  "project.fault_event.batch_status_change.modal.form.change_status.impacted_period":
    "Période affichée :",
  "project.fault_event.batch_status_change.modal.form.change_status.nb_impacted_detections":
    "Détections sur l’événement impactées :",
  "project.fault_event.chart.evolution.title": "Évolution de l'impact",
  "project.fault_event.chart.evolution.title.bagde.closable": "Clôturable",
  "project.fault_event.chart.evolution.title.bagde.significative_increase":
    "Évolution significative",
  "project.fault_event.chart.magnitude.axis.title": (faultName: string, unit: string) =>
    `Amplitude ${faultName} (${unit})`,
  "project.fault_event.common.select.prompt": "Sélectionner",
  "project.fault_event.dashboard_select.create": (translations: CustomTranslations) =>
    `Créer un ${translations.FR.DASHBOARD.toLowerCase()} (partagé par défaut)`,
  "project.fault_event.dashboard_select.empty": (translations: CustomTranslations) =>
    `Aucun ${translations.FR.DASHBOARD.toLowerCase()} n’a été partagé dans le projet.`,
  "project.fault_event.detection.false": "Fausse détection",
  "project.fault_event.detection.true": "Vraie détection",
  "project.fault_event.diagnosis.symptoms.chart.observables": "Observables",
  "project.fault_event.diagnosis.symptoms.chart.performance_indicators":
    "Indicateurs de performance",
  "project.fault_event.evolution_chart.batch_status_change.alert_icon.tooltip.linkText":
    "Relancer le diagnostic",
  "project.fault_event.evolution_chart.batch_status_change.alert_icon.tooltip.text":
    "Aucun diagnostic n'a été relancé suite à cette mise en fausse détection",
  "project.fault_event.evolution_chart.batch_status_change.change": "Changer le statut",
  "project.fault_event.evolution_chart.batch_status_change.displayed_period.description":
    "La synthèse et les modifications de statut s’appliquent uniquement à l’événement en cours pour la période affichée.",
  "project.fault_event.evolution_chart.batch_status_change.displayed_period.title": ({
    period,
  }: {
    period: ReactNode;
  }) => <>Statut des détections pour la période affichée : {period}</>,
  "project.fault_event.evolution_chart.batch_status_change.modal.change_status.title":
    "Changer le statut",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.additionnal_message.update_fault_status":
    (status: string) => (
      <>
        Suite à cette action, l’événement passera en statut &quot;<strong>{status}</strong>&quot;.
      </>
    ),
  "project.fault_event.evolution_chart.batch_status_change.modal.form.additionnal_message.update_fault_status_and_closed_event":
    "Suite à cette action, l’événement sera clôturé et indiqué en fausse détection.",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.button.name":
    "Changer",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.checkbox.apply_to_new_detections":
    "Appliquer ce statut automatiquement aux nouvelles détections.",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.checkbox.apply_to_new_detections.disabled":
    "Option non disponible tant que le status n’a pas changé",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.relunch_diagnosis":
    "Relancer un diagnostic sans prendre en compte cette défaillance.",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.toast.update_status":
    "Les statuts ont été mis à jour",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.tooltip.apply_to_new_detections.already_set":
    "Ce statut est déjà défini comme étant appliqué aux nouvelles détections.",
  "project.fault_event.evolution_chart.batch_status_change.no_detection": "Aucune détection",
  "project.fault_event.evolution_chart.batch_status_change.reintegrate":
    "Réintégrer dans le diagnostic",
  "project.fault_event.evolution_chart.batch_status_change.repeat_icon.tooltip.text":
    "Le statut est appliqué automatiquement aux nouvelles détections",
  "project.fault_event.evolution_chart.batch_status_change.status_applied_new_detections":
    "Statut appliqué aux nouvelles détections :",
  "project.fault_event.evolution_chart.batch_status_change.title": ({
    status,
    count,
  }: {
    status: string;
    count: number;
  }) => `${status} : ${count}`,
  "project.fault_event.evolution_chart.change_fault_event_status.modal.form.toast.success":
    "Modifications enregistrées avec succès.",
  "project.fault_event.evolution_chart.detections_status_change.modal.form.additionnal_message.repeat_detection":
    "Ce statut sera appliqué aux prochaines détections.",
  "project.fault_event.evolution_chart.detections_status_change.modal.form.button.name":
    "Sauvegarder",
  "project.fault_event.evolution_chart.detections_status_change.modal.form.description": ({
    toAnalysed,
  }: {
    toAnalysed: ReactNode;
  }) => (
    <>
      Le statut sera appliqué automatiquement aux nouvelles détections. Avant toute modification, le
      statut par défaut est {toAnalysed}.
    </>
  ),
  "project.fault_event.evolution_chart.detections_status_change.modal.title":
    "Statut appliqué aux nouvelles détections",
  "project.fault_event.fault.chart.period_picker.reset": "Zoomer sur l'événement",
  "project.fault_event.fault.evolution.overview.header.option.amplitude": "Amplitude",
  "project.fault_event.fault.evolution.overview.header.option.amplitude.description":
    "Cet affichage présente l’évolution de l’amplitude de la défaillance, telle qu’identifiée par l’algorithme de diagnostic.",
  "project.fault_event.fault.evolution.overview.header.option.impact": "Impact",
  "project.fault_event.fault.hearder.editing.name.error.cannot_be_empty":
    "Le nom de l’événement ne peut pas être vide",
  "project.fault_event.fault.hearder.editing.name.error.cannot_exceed_100_characters":
    "Le nom ne peut pas dépasser 100 caractères",
  "project.fault_event.fault.hearder.editing.name.error.must_be_unique":
    "Un événement avec le même nom existe déjà",
  "project.fault_event.fault.subtitle.date": (
    duration: string | null,
    firstDetection: string,
    lastDetection: string | null,
  ) => {
    if (duration === null || lastDetection === null) {
      return `Première détection : ${firstDetection}`;
    }
    return `Durée : ${duration} (première détection : ${firstDetection} - dernière détection : ${lastDetection})`;
  },
  "project.fault_event.fault.subtitle.identified_actions": "Actions identifiées",
  "project.fault_event.fault.subtitle.name": "Défaillance :",
  "project.fault_event.header.badge_event_status.tooltip.text":
    "Cliquez sur le badge pour changer le statut de l’événement",
  "project.fault_event.header.button.comment": "Commenter",
  "project.fault_event.header.button.edit": "Modifier",
  "project.fault_event.header.last_event_comment.button.access_comment": "Accéder aux commentaires",
  "project.fault_event.header.last_event_comment.publishedAt.access_comment": ({
    relativeDate,
  }: {
    relativeDate: string;
  }) => `a écrit ${relativeDate}`,
  "project.fault_event.header.link.history": "Historique",
  "project.fault_event.header.menu.option.merge": "Fusionner avec un autre événement",
  "project.fault_event.header.menu.option.merge.disabled":
    "Fusion impossible car aucun autre événement n'est lié à cette défaillance dans le projet.",
  "project.fault_event.header.menu.option.rename": "Modifier le nom",
  "project.fault_event.header.menu.option.split": "Diviser l’événement en deux",
  "project.fault_event.header.menu.option.split.disabled":
    "Cet événement ne dure qu’un jour et ne peut pas être divisé.",
  "project.fault_event.header.menu.option.split.disabled.multi_failure":
    "Un événement multi-défaillance ne peut pas être divisé.",
  "project.fault_event.header.menu.option.split.disabled.status.closed":
    "Cet événement est clôturé et ne peut pas être modifié.",
  "project.fault_event.header.menu.option.ungroup": "Défusionner",
  "project.fault_event.header.menu.tag.inactive_event": "Inactif",
  "project.fault_event.header.navigation.dashboard_tab.tooltip.icon.add_dashboard": (
    translations: CustomTranslations,
  ) => `Ajouter un ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.fault_event.header.navigation.dashboard_tab.tooltip.icon.remove_dashboard": "Retirer",
  "project.fault_event.header.navigation.dashboard_tab.tooltip.icon.see_more": "Voir plus",
  "project.fault_event.header.navigation.modal_form.add_dashboard.select.prompt": "Sélectionner",
  "project.fault_event.header.navigation.modal_form.remove_dashboard.text": (
    {
      dashboardName,
    }: {
      dashboardName: ReactNode;
    },
    translations: CustomTranslations,
  ) => (
    <>
      Êtes-vous sûr de vouloir retirer le {translations.FR.DASHBOARD.toLowerCase()} {dashboardName}{" "}
      ? <br /> Cette action n&apos;affectera pas le {translations.FR.DASHBOARD.toLowerCase()}{" "}
      lui-même, mais il ne sera plus visible depuis cet événement.
    </>
  ),
  "project.fault_event.header.navigation.modal_form.remove_dashboard.title": (
    translations: CustomTranslations,
  ) => `Retirer le ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.fault_event.live.widget.average_losses.title": ({
    translations,
  }: {
    translations: {
      FR: {
        LOSSES: string;
        AVERAGE: string;
      };
      EN: {
        LOSSES: string;
        AVERAGE: string;
      };
    };
  }) => `${capitalize(translations.FR.LOSSES)} ${translations.FR.AVERAGE.toLowerCase()}`,
  "project.fault_event.live.widget.average_losses.tooltip": (
    translations: {
      FR: {
        LOSSES: string;
        AVERAGE: string;
      };
      EN: {
        LOSS: string;
        AVERAGE: string;
      };
    },
    durationISO8601: string,
  ) => {
    const lossesLower = translations.FR.LOSSES.toLowerCase();

    return `Les valeurs affichées sont calculées sur la base des valeurs de ${lossesLower} pour les analyses présentes sur ${durationISO8601} (valeur paramétrée au niveau de l'environnement). La valeur moyenne présentée pondère les valeurs de ${lossesLower} en fonction de la durée entre 2 points. La comparaison est effectuée par rapport à la valeur prise sur la période précédente de même durée.`;
  },
  "project.fault_event.live.widget.average_losses.value.description": ({
    percent,
    total,
    unit,
  }: {
    percent: string;
    total: string;
    unit: string | null;
  }) => `${percent} % des ${total} ${unit} mesurés`,
  "project.fault_event.live.widget.average_losses.value.trend_indicator": ({
    evolution,
  }: {
    evolution: ReactNode;
  }) => <>{evolution} comparé à la période précédente</>,
  "project.fault_event.live.widget.event_priority.calculation_info": "Calculé avec Metroscope IA",
  "project.fault_event.live.widget.event_priority.empty": "Aucun événement actif",
  "project.fault_event.live.widget.event_priority.title": "Priorité des événements",
  "project.fault_event.live.widget.event_priority.tooltip":
    "Uniquement les événements actifs sont pris en compte dans le calcul. La priorité de l'événement est calculée en fonction de plusieurs critères (impact sur l'indicateur de performance, niveau de confiance de la détection, fréquence de détection). Elle peut être modifiée manuellement.",
  "project.fault_event.live.widget.losses_repartition.title"({
    translations,
  }: {
    translations: {
      FR: {
        LOSSES: string;
      };
      EN: {
        LOSSES: string;
      };
    };
  }) {
    return `Répartition des ${translations.FR.LOSSES.toLowerCase()}`;
  },
  "project.fault_event.live.widget.losses_repartition.tooltip": (
    translations: {
      FR: {
        LOSSES: string;
        AVERAGE: string;
      };
      EN: {
        LOSS: string;
        AVERAGE: string;
      };
    },
    durationISO8601: string,
  ) => {
    const lossesLower = translations.FR.LOSSES.toLowerCase();
    const averageLower = translations.FR.AVERAGE.toLowerCase();

    return `Les valeurs affichées sont calculées à partir des ${lossesLower} des analyses présentes sur la période ${durationISO8601}. Elles correspondent, pour chaque événement, à une agrégation par système des ${lossesLower} ${averageLower}. Seuls les trois systèmes ayant les ${lossesLower} les plus importantes sont présentés.`;
  },
  "project.fault_event.loading":
    "Le contenu est en train de se charger, cela peut prendre quelques instants.",
  "project.fault_event.modal.form.change_status.comment.label": "Commentaire",
  "project.fault_event.modal.form.change_status.comment.placeholder": "Ajouter un commentaire",
  "project.fault_event.modal.form.change_status.comment.sub_comment":
    "Ce commentaire sera enregistré dans le fil de commentaires de l’événement.",
  "project.fault_event.modal.form.change_status.input.action_identified.error":
    "Ce champ ne peut pas dépasser 4000 caractères.",
  "project.fault_event.modal.form.change_status.input.action_identified.label":
    "Actions identifiées",
  "project.fault_event.modal.form.change_status.input.action_identified.placeholder":
    "Mentionner ici les actions identifiées (e.g., lien vers la (ou les) demande(s) de travaux)",
  "project.fault_event.modal.form.datePicker.split_date.info": (date: ReactNode) => (
    <>La date doit être comprise entre : {date}</>
  ),
  "project.fault_event.modal.form.datePicker.split_date.label": "Date de scission",
  "project.fault_event.modal.form.group_events.button.name.ok": "Regrouper",
  "project.fault_event.modal.form.group_events.input.error.empty_field":
    "Le nom de l’événement ne doit pas être vide.",
  "project.fault_event.modal.form.group_events.input.name.hint":
    "Le suffixe correspondant à la date de début la plus ancienne est ajouté automatiquement.",
  "project.fault_event.modal.form.group_events.input.name.label": "Nom du groupe d’événements",
  "project.fault_event.modal.form.group_events.summary.events": (count: number) =>
    `Événements à regrouper (${count})`,
  "project.fault_event.modal.form.group_events.summary.events.endsAt": "Date de fin",
  "project.fault_event.modal.form.group_events.summary.events.name": "Nom",
  "project.fault_event.modal.form.group_events.summary.events.startsAt": "Date de début",
  "project.fault_event.modal.form.group_events.summary.name": (name: string) =>
    `Nom de l’événement regroupé : ${name}`,
  "project.fault_event.modal.form.group_events.toast.success":
    "Les événements ont été regroupés avec succès.",
  "project.fault_event.modal.form.input.fault.name.error.cannot_exceed_100_characters":
    "Le nom de l'événement ne peut pas dépasser 100 caractères",
  "project.fault_event.modal.form.merge_event.button.name.ok": "Fusionner",
  "project.fault_event.modal.form.merge_event.input.error.empty_field":
    "Le nom de l’événement ne doit pas être vide.",
  "project.fault_event.modal.form.merge_event.input.new_event.label": "Nom du nouvel événement",
  "project.fault_event.modal.form.merge_event.message.redirection":
    "Après confirmation, vous serez redirigé vers la page du nouvel événement.",
  "project.fault_event.modal.form.merge_event.select.label": "Événement à fusionner avec",
  "project.fault_event.modal.form.merge_event.select.tooltip":
    "Il est possible de fusionner avec un événement lié à la même défaillance, situé immédiatement avant ou après.",
  "project.fault_event.modal.form.merge_event.summary.comments": (
    comments: ReactNode,
    count: number,
  ) => (
    <>
      Commentaire{count > 1 ? "s" : ""} : {comments}
    </>
  ),
  "project.fault_event.modal.form.merge_event.summary.dashboards": (
    dashboard: ReactNode,
    count: number,
  ) => (
    <>
      Tableau{count > 1 ? "x" : ""} de bord associé
      {count > 1 ? "s" : ""} : {dashboard}
    </>
  ),
  "project.fault_event.modal.form.merge_event.summary.date": (date: ReactNode) => (
    <>Dates : {date}</>
  ),
  "project.fault_event.modal.form.merge_event.summary.event_status": (status: ReactNode) => (
    <>Statut de l’événement : {status}</>
  ),
  "project.fault_event.modal.form.merge_event.summary.name": (name: ReactNode) => <>Nom : {name}</>,
  "project.fault_event.modal.form.merge_event.summary.newDiagnosesStatus": (status: ReactNode) => (
    <>Statut appliqué aux nouvelles détections : {status}</>
  ),
  "project.fault_event.modal.form.merge_event.summary.reparability": (reparability: ReactNode) => (
    <>Réparabilité : {reparability}</>
  ),
  "project.fault_event.modal.form.merge_event.summary.title": "Récapitulatif du nouvel événement :",
  "project.fault_event.modal.form.merge_event.toast.success":
    "Les événements ont été fusionnés avec succès.",
  "project.fault_event.modal.form.split_event.button.name.ok": "Diviser",
  "project.fault_event.modal.form.split_event.description": (eventName: ReactNode) => (
    <>
      Vous êtes sur le point de diviser l’événement {eventName} en deux. Choisissez la date de
      scission et définissez les noms pour chaque événement.
    </>
  ),
  "project.fault_event.modal.form.split_event.input.error.empty_field":
    "Le nom de l’événement ne doit pas être vide.",
  "project.fault_event.modal.form.split_event.input.next_event.default_name": (name: string) =>
    `${name} - Partie 2`,
  "project.fault_event.modal.form.split_event.input.next_event.hint":
    "Nouvel événement, créé à partir de la date de scission",
  "project.fault_event.modal.form.split_event.input.next_event.label": "Nom du second événement",
  "project.fault_event.modal.form.split_event.input.previous_event.default_name": (name: string) =>
    `${name} - Partie 1`,
  "project.fault_event.modal.form.split_event.input.previous_event.hint":
    "Événement existant, conservé jusqu’à la date de scission (exclus)",
  "project.fault_event.modal.form.split_event.input.previous_event.label":
    "Nom du premier événement",
  "project.fault_event.modal.form.split_event.message.redirection":
    "Après confirmation, vous serez redirigé vers la page du second événement.",
  "project.fault_event.modal.form.split_event.toast.success":
    "L'événement a été divisé en deux avec succès.",
  "project.fault_event.modal.form.ungroup_event.button.name.ok": "Défusionner",
  "project.fault_event.modal.form.ungroup_event.summary.failures": "Défaillances concernées",
  "project.fault_event.modal.form.ungroup_event.summary.name": (name: ReactNode) => (
    <>Événement fusionné : {name}</>
  ),
  "project.fault_event.modal.form.ungroup_event.summary.source_fallback": (index: number) =>
    `Événement source ${index}`,
  "project.fault_event.modal.form.ungroup_event.summary.sources":
    "Événements sources persistés qui seront restaurés",
  "project.fault_event.modal.form.ungroup_event.toast.success":
    "L’événement a été défusionné avec succès.",
  "project.fault_event.modal.form.ungroup_event.warning.comments_lost":
    "Les commentaires directs du multi-défaillance seront perdus.",
  "project.fault_event.modal.merge_event.title": "Fusionner les événements",
  "project.fault_event.modal.split_event.title": "Diviser l’événement en deux",
  "project.fault_event.modal.ungroup_event.title": "Défusionner l’événement",
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.false.not_all_faults_false_detection":
    "Toutes les détections ne sont pas mises en fausse détection et une fois l’événement clôturé, il ne sera plus possible de modifier les statuts.",
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.false.not_all_faults_false_detection_and_not_be_automatically_false_detection":
    ({ statusToNewDetection }: { statusToNewDetection: string }) => (
      <>
        Toutes les détections ne sont pas mises en fausse détection et une fois l’événement clôturé,
        il ne sera plus possible de modifier les statuts. Par ailleurs, le statut appliqué aux
        nouvelles détections étant &quot;{statusToNewDetection}&quot;, elles ne seront pas
        automatiquement mises en fausse détection.
      </>
    ),
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.false.not_be_automatically_false_detection":
    ({ statusToNewDetection }: { statusToNewDetection: string }) => (
      <>
        Le statut appliqué aux nouvelles détections étant &quot;{statusToNewDetection}&quot;, elles
        ne seront pas automatiquement mises en fausse détection.
      </>
    ),
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.true.not_all_faults_confirmed":
    "Toutes les détections ne sont pas confirmées et une fois l’événement clôturé, il ne sera plus possible de modifier les statuts.",
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.true.not_all_faults_confirmed_and_not_be_automatically_confirmed":
    ({ statusToNewDetection }: { statusToNewDetection: string }) => (
      <>
        Toutes les détections ne sont pas confirmées et une fois l’événement clôturé, il ne sera
        plus possible de modifier les statuts. Par ailleurs, le statut appliqué aux nouvelles
        détections étant &quot;{statusToNewDetection}&quot;, elles ne seront pas automatiquement
        confirmées.
      </>
    ),
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.true.not_be_automatically_confirmed":
    ({ statusToNewDetection }: { statusToNewDetection: string }) => (
      <>
        Le statut appliqué aux nouvelles détections étant &quot;{statusToNewDetection}&quot;, elles
        ne seront pas automatiquement confirmées.
      </>
    ),
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.true_or_false":
    "Êtes-vous sûr de vouloir clôturer cet événement en l’état ?",
  "project.fault_event.modal_form.checkbox.detection_type.false_detection": "Fausse détection",
  "project.fault_event.modal_form.select.detection_type.label": "Type de détection",
  "project.fault_event.modal_form.select.outage_type_for_repair.label": "Réparabilité",
  "project.fault_event.modal_form.select.status.label": "Statut",
  "project.fault_event.modale.modal.change_status.title": "Changer le statut",
  "project.fault_event.modale.modal.update_status.ok_button_name": "Changer",
  "project.fault_event.priority.high": "Haut",
  "project.fault_event.priority.low": "Bas",
  "project.fault_event.priority.medium": "Moyen",
  "project.fault_event.quantity_selector.category.others": "Autre",
  "project.fault_event.quantity_selector.category.promotion_filter":
    "Filtre incitatif de défaillance",
  "project.fault_event.quantity_selector.switch.display_all_faults.tooltip.disabled":
    "Indisponible en mode Amplitude",
  "project.fault_event.quantity_selector.switch.display_key_quantities.label":
    "Afficher toutes les grandeurs",
  "project.fault_event.quantity_selector.switch.display_key_quantities.off.tooltip.text":
    "Par défaut, seuls les indicateurs de performance et les observables les plus impactées par la défaillance (top 8) sont affichées.",
  "project.fault_event.quantity_selector.switch.display_key_quantities.on.tooltip.text":
    "Par défaut, les indicateurs de performance, les observables les plus impactées par la défaillance (top 8) et toutes les grandeurs utilisées dans leurs calculs sont affichés.",
  "project.fault_event.side_panel.activity.title": "Activité",
  "project.fault_event.side_panel.details.category": "Catégorie",
  "project.fault_event.side_panel.details.degradation_type": "Type de dégradation",
  "project.fault_event.side_panel.details.description": "Description",
  "project.fault_event.side_panel.details.equipment": "Équipements",
  "project.fault_event.side_panel.details.equipment_type": "Type d’équipement",
  "project.fault_event.side_panel.details.fault_name": "Nom de la défaillance",
  "project.fault_event.side_panel.details.fault_type": "Incitée",
  "project.fault_event.side_panel.details.hitory.tooltip.info": () => (
    <>
      <strong>Défaillance</strong> : inclut les informations relatives à la défaillance, tel que
      défini dans le paramétrage. <br />
      <strong>Historique</strong> : présente les autres événements liés à la même défaillance (liste
      restreinte aux projets de production et aux événements avec un statut différent de &quot;À
      analyser&quot;).
    </>
  ),
  "project.fault_event.side_panel.details.suspected_elements": "Éléments suspectés",
  "project.fault_event.side_panel.details.system": "Système",
  "project.fault_event.side_panel.details.tooltip.fault_filter_type":
    "Cette défaillance est incitée lorsque certaines grandeurs respectent des critères, tels que définis dans le paramétrage.",
  "project.fault_event.side_panel.fault_event_details.input.name": "Nom de l’événement",
  "project.fault_event.side_panel.fault_event_details.select.priority": "Priorité",
  "project.fault_event.side_panel.fault_event_details.select.priority.hint": (priority: string) => (
    <>
      La priorité a été modifiée manuellement. La valeur calculée automatiquement est{" "}
      <strong>{priority}</strong>.
    </>
  ),
  "project.fault_event.side_panel.fault_event_details.select.priority.tooltip":
    "La priorité de l'événement est calculée en fonction de plusieurs critères (impact sur l'indicateur de performance, niveau de confiance de la détection, fréquence de détection). Elle peut être modifiée manuellement.",
  "project.fault_event.side_panel.fault_event_details.title": "Détails de l’événement",
  "project.fault_event.side_panel.history.blank_state.no_events_found.subtitle":
    "Essayez de modifier vos filtres.",
  "project.fault_event.side_panel.history.blank_state.no_events_found.title":
    "Aucun événement trouvé",
  "project.fault_event.side_panel.history.blank_state.subtitle":
    "Si un événement similaire avec un statut différent de 'À analyser' est détecté sur un projet de production, le lien vers cet événement apparaîtra ici.",
  "project.fault_event.side_panel.history.blank_state.title": "Aucun événement similaire détecté",
  "project.fault_event.side_panel.history.card.start_date": ({
    isClosed,
    startDate,
    endDate,
  }: {
    isClosed: boolean;
    startDate: string;
    endDate: string;
  }) => (isClosed ? `${startDate} - ${endDate}` : `Début : ${startDate}`),
  "project.fault_event.side_panel.history.current_project": "Ce projet",
  "project.fault_event.side_panel.tab.fault": (count: number): string =>
    count > 1 ? "Défaillances" : "Défaillance",
  "project.fault_event.side_panel.title": "Information sur la défaillance",
  "project.fault_event.status.action_identified": "Action identifiée",
  "project.fault_event.status.closed": "Clôturé",
  "project.fault_event.status.on_going": "Analyse en cours",
  "project.fault_event.status.to_be_analyzed": "À analyser",
  "project.fault_event.tab.data": "Données",
  "project.fault_event.tab.fault": "Impact",
  "project.fault_event.tab.symptom": "Symptômes",
  "project.fault_event.tooltips.analysis_to_analyze": () => (
    <>Cet événement contient au moins une détection avec le statut &quot;À analyser&quot;</>
  ),
  "project.fault_event.tooltips.single": "Événement avec 1 seule détection",
  "project.fault_events.button.export": "Exporter en .csv",
  "project.fault_events.charts.losses_repartition.all_around_zero":
    "Valeurs présentes mais trop faibles pour être affichées.",
  "project.fault_events.charts.losses_repartition.categories.system": "Système",
  "project.fault_events.charts.losses_repartition.categories.total": "Pertes internes",
  "project.fault_events.charts.losses_repartition.label.internal_losses": "Total",
  "project.fault_events.charts.losses_repartition.label.other_systems": "Autres",
  "project.fault_events.charts.losses_repartition.label.unexplained_losses": (translations: {
    FR: {
      UNEXPLAINED: string;
    };
    EN: {
      UNEXPLAINED: string;
    };
  }) => `${capitalize(translations.FR.UNEXPLAINED)}`,
  "project.fault_events.closed.blank_state.subtitle":
    "Une fois les événements clôturés, ils seront listés ici",
  "project.fault_events.closed.blank_state.title": "Aucun événement clôturé",
  "project.fault_events.export.message.no_checkbox_selected":
    "Veuillez sélectionner au moins un statut d’événement pour poursuivre l’export.",
  "project.fault_events.grouping.button": (count: number) => `Regrouper (${count})`,
  "project.fault_events.grouping.modal.title": "Regrouper les événements",
  "project.fault_events.live.blank_state.subtitle":
    "Les événements à traiter apparaîtront ici dès qu'ils seront disponibles",
  "project.fault_events.live.blank_state.title": "Aucun événement à traiter",
  "project.fault_events.live.button.create_manual_event": "Événement manuel",
  "project.fault_events.live.section.priority.empty": (prioriry: PriorityFaultEvent): string => {
    if (prioriry === "HIGH") {
      return "Aucun événement en haute priorité";
    } else if (prioriry === "MEDIUM") {
      return "Aucun événement en moyenne priorité";
    } else {
      return "Aucun événement en basse priorité";
    }
  },
  "project.fault_events.loading": "Chargement en cours",
  "project.fault_events.modal.title.export": "Exporter les événements",
  "project.fault_events.modal_form.export.text": () => (
    <>
      Les événements (diagnostiqués et manuels) et les détails associés seront exportés sous forme
      de fichier .csv.
      <br />
      Sélectionnez les statuts des événements à inclure dans le fichier exporté :
    </>
  ),
  "project.fault_events.selector.graph": "Graphe",
  "project.fault_events.selector.graph_tooltip": "La vue graphique est en cours de développement.",
  "project.fault_events.selector.table": "Tableau",
  "project.fault_events.tab.closed": "Clôturés",
  "project.fault_events.tab.closed.detection": (detection: boolean): string =>
    detection ? "Vraie" : "Fausse",
  "project.fault_events.tab.in_progress": "En cours",
  "project.fault_events.table.average_losses.no_sparkline": (duration: string) =>
    `Pas de détection sur ${duration}`,
  "project.fault_events.table.closed.metadata.detection": "Détection",
  "project.fault_events.table.closed.metadata.end_date": "Date de clôture",
  "project.fault_events.table.closed.metadata.name": "Nom",
  "project.fault_events.table.closed.metadata.reparability": "Réparabilité",
  "project.fault_events.table.closed.metadata.start_date": "Première détection",
  "project.fault_events.table.closed.metadata.system": "Système",
  "project.fault_events.table.live.action.ungroup": "Défusionner",
  "project.fault_events.table.live.average_losses.tooltip.info": (
    translations: {
      FR: {
        LOSSES: string;
      };
      EN: {
        LOSS: string;
      };
    },
    durationISO8601: string,
    showPartLoadSensitiveKpis: boolean,
  ) => {
    const lossesLower = translations.FR.LOSSES.toLowerCase();

    if (!showPartLoadSensitiveKpis) {
      return `La courbe affichée se base sur les détections sur ${durationISO8601} (valeur paramétrée au niveau de l'environnement).`;
    }

    return `La moyenne et la courbe affichée se basent sur les détections sur ${durationISO8601} (valeur paramétrée au niveau de l'environnement). La valeur moyenne présentée pondère les valeurs de ${lossesLower} en fonction de la durée entre 2 points et considère une absence de détection comme une valeur de ${lossesLower} nulle.`;
  },
  "project.fault_events.table.live.metadata.average_losses": (
    translations: {
      FR: {
        LOSSES: string;
        AVERAGE: string;
      };
      EN: {
        LOSSES: string;
        AVERAGE: string;
      };
    },
    duration: string,
  ) =>
    `${capitalize(translations.FR.LOSSES)} ${translations.FR.AVERAGE.toLowerCase()} sur ${duration}`,
  "project.fault_events.table.live.metadata.comments": "Commentaires",
  "project.fault_events.table.live.metadata.end_date": "Dernière détection",
  "project.fault_events.table.live.metadata.last_activity_date": "Dernière activité",
  "project.fault_events.table.live.metadata.last_comment.tooltip.content": ({
    author,
    analysis,
    publishedAt,
    content,
  }: {
    author: string;
    analysis: string | null;
    publishedAt: string;
    content: ReactNode;
  }) => (
    <>
      {author} a écrit {publishedAt} {analysis ? `par rapport à l’analyse ${analysis} ` : ""}:
      <br />
      {content}
    </>
  ),
  "project.fault_events.table.live.metadata.last_comment.tooltip.title": "Dernier commentaire",
  "project.fault_events.table.live.metadata.name": "Nom",
  "project.fault_events.table.live.metadata.name.icon.inactive.title": "Événement inactif",
  "project.fault_events.table.live.metadata.name.tooltip.button.inactive_event": "Rendre inactif",
  "project.fault_events.table.live.metadata.reparability": "Réparabilité",
  "project.fault_events.table.live.metadata.start_date": "Première détection",
  "project.fault_events.table.live.metadata.status": "Statut",
  "project.fault_events.table.live.metadata.system": "Système",
  "project.fault_events.table.live.metadata.system.multiple": (nbSelection: number) =>
    `Système${nbSelection > 1 ? "s" : ""} : `,
  "project.fault_events.table.live.metadata.work_request": "Actions identifiées",
  "project.fault_events.table.live.switch.cold_events.text": "Afficher les événements inactifs",
  "project.fault_events.table.live.switch.cold_events.tooltip.text":
    "Aucune activité lors des trois derniers mois ou 1 seule détection",
  "project.fault_events.table.live.tooltips.closable":
    "Événement clôturable (aucune détection liée à cet événement sur les trois derniers mois)",
  "project.fault_events.table.live.tooltips.significant_evolution.high":
    "Hausse significative de l’impact (augmentation de plus de 20% entre les 10 dernières détections et les 10 précédentes)",
  "project.fault_events.table.live.tooltips.significant_evolution.low":
    "Baisse significative de l’impact (diminution de plus de 20% entre les 10 dernières détections et les 10 précédentes)",
  "project.fault_events.title": "Événements",
  "project.fault_events.widgets.no_data_in_period_placeholder":
    "Aucune donnée disponible pour cette période",
  "project.fault_events.widgets.no_data_placeholder": "Aucune donnée disponible",
  "project.fault_events.widgets.no_pi_placeholder":
    "Il est de nécessaire définir au moins un indicateur de performance pour afficher le widget des pertes moyennes.",
  "project.manual_fault_event.edit_form.system.info":
    "Modifier ce système met à jour le problème connu utilisé par tous les événements manuels associés.",
  "project.manual_fault_event.edit_form.system.label": "Système",
  "project.manual_fault_event.header.menu.option.delete": "Supprimer",
  "project.manual_fault_event.header.modal_form.remove.success": (name: string) => (
    <>
      La suppression de l’événement <strong>{name}</strong> a réussi
    </>
  ),
  "project.manual_fault_event.modal.delete.title": "Supprimer l’événement manuel",
  "project.manual_fault_event.modal_form.remove.text": (name: ReactNode) => (
    <>
      Vous vous apprêtez à supprimer définitivement l’événement {name}. Cet événement n’apparaîtra
      plus dans la liste des événements, et l&apos;impact associé n&apos;apparaîtra plus dans les
      différents graphes.
    </>
  ),
  "project.manual_fault_event.tag.manual": "Manuel",
  "project.widget.average_losses.evolution.state": (
    evolutionState: "stable" | "improvement" | "deterioration",
  ): string => {
    switch (evolutionState) {
      case "stable":
        return "Stabilité";
      case "improvement":
        return "Amélioration";
      case "deterioration":
        return "Dégradation";
      default:
        throw new Error("Invalid evolution state");
    }
  },
  "project.widget.average_losses.evolution.value": (evolutionValue: string): string =>
    `(période précédente : ${evolutionValue})`,
  "projet.events.create_manual_fault_event.drawer.title": "Créer un événement manuel",
};

export default fr;
